// FM-08 — a behavioural or operational assumption with no cited source.
//
// FEMP M&V Guidelines 5.0 §4.2.1.2 publishes both a permitted and a prohibited
// list of sources for estimated parameters. That is what makes this check
// deterministic rather than a judgement: the question is not whether an estimate
// is reasonable, it is whether any permitted source is cited near it.
//
// Prohibited, in FEMP's own words: rules of thumb, black-box algorithms,
// handshake agreements, guesses at operating parameters, undocumented
// assumptions, and equations derived from questionable data.

import type { Flag, FlagDraft, SourceDocument } from '../../types';
import { evidenceFor, detectionSignal, tierOf } from '../../data/taxonomy';
import { buildLocator, sectionRefOf } from './locate';
import { enrichFlag } from '../finding';

export const CHECK_ID = 'source-attribution-check';

/** How a plan marks a value it did not measure. */
const MARKERS: { label: string; pattern: RegExp }[] = [
  { label: '"Est."', pattern: /\bEst\./g },
  { label: '"estimated"', pattern: /\bestimated\b/gi },
  { label: '"assumed"', pattern: /\bassumed\b/gi },
  { label: '"stipulated"', pattern: /\bstipulated\b/gi },
  // "typical" counts only where it qualifies a quantity. "typical operating
  // temperature" describes the condition a meter reading was taken under, not
  // an estimated parameter — flagging that was a false positive.
  { label: '"typical"', pattern: /\btypical\b(?=[^.\n]{0,60}\d)/gi },
  { label: '"based on experience"', pattern: /\bbased on experience\b/gi },
];

/**
 * The permitted-source types, as phrases rather than bare words. "measured"
 * alone appears in table headers and column labels all over a plan; requiring an
 * actual attribution keeps the check from being silently switched off by a
 * heading.
 *
 * Five of these are the list the check catalogue carries for FM-08, drawn from
 * FEMP M&V Guidelines 5.0 §4.2.1.2: measured models, manufacturer data, industry
 * curves, TMY, O&M logs. "Observation of building or occupant behavior" is this
 * tool's addition and is not in that list, so the check attributes the five to
 * the guidelines and names the sixth as ours. The check is the surface a reviewer
 * reads line by line; it cannot cite a standard for something the standard was
 * not shown to say.
 */
const PERMITTED: { label: string; pattern: RegExp }[] = [
  {
    label: 'measured data or a measured model',
    pattern:
      /\b(?:as measured|measured (?:value|values|data|model|parameter|power|hours|load|demand|wattages?|lighting)|(?:power|wattage|instantaneous|kW|baseline) measurements?|measurements? (?:were|was|will be) (?:taken|made|performed|recorded)|monitored (?:for|over|during)|monitored (?:baseline )?(?:data|hours|operating hours|values?)|based on monitored|short-term monitoring|logger|metered data|kW metering)\b/i,
  },
  {
    label: 'manufacturer data or curves',
    pattern:
      /\b(?:manufacturer(?:'|’)?s?\s*(?:data|specification|specifications|literature|curve|curves|catalog|catalogue|submittal)|nameplate)\b/i,
  },
  {
    label: 'industry-accepted curves (ANSI, AHRI, ASHRAE)',
    pattern: /\b(?:ANSI|AHRI|ARI|ASHRAE|IPMVP)\b/i,
  },
  {
    label: 'TMY weather data',
    pattern: /\b(?:TMY\d?|typical meteorological year|NOAA|weather (?:data|station))\b/i,
  },
  {
    label: 'observation of building or occupant behaviour',
    pattern:
      /\b(?:observed|observation[s]? of|site (?:visit|survey|inspection)|walk-?through|occupant survey|as-?built (?:survey|inventory)|inventory)\b/i,
  },
  { label: 'O&M logs', pattern: /\b(?:O&M log|maintenance log|operating log|operations log|log ?sheets?)\b/i },
];

/** Words after which "typical" is a permitted source, not an unsourced estimate. */
const TYPICAL_EXEMPT = /typical meteorological year/i;

const WINDOW = 400;

interface Occurrence {
  index: number;
  length: number;
  marker: string;
  matched: string;
}

export function sourceAttributionCheck(doc: SourceDocument): Flag[] {
  const text = doc.rawText;
  const locator = buildLocator(doc);
  const occurrences: Occurrence[] = [];

  for (const { label, pattern } of MARKERS) {
    pattern.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = pattern.exec(text)) !== null) {
      if (label === '"typical"') {
        const tail = text.slice(m.index, m.index + 40);
        if (TYPICAL_EXEMPT.test(tail)) continue;
      }
      occurrences.push({
        index: m.index,
        length: m[0].length,
        marker: label,
        matched: m[0],
      });
    }
  }

  // Keep only the ones with no permitted source in reach.
  const unsourced = occurrences.filter((occ) => {
    const from = Math.max(0, occ.index - WINDOW);
    const to = Math.min(text.length, occ.index + occ.length + WINDOW);
    const window = text.slice(from, to);
    return !PERMITTED.some(({ pattern }) => pattern.test(window));
  });

  // One flag per failure mode per section. The section is the unit a reviewer
  // reads, and four unsourced values in one table are one finding, not four.
  const bySection = new Map<string, Occurrence[]>();
  for (const occ of unsourced) {
    const section = locator.sectionOf(occ.index);
    const ref = sectionRefOf(section);
    const list = bySection.get(ref);
    if (list) list.push(occ);
    else bySection.set(ref, [occ]);
  }

  const flags: FlagDraft[] = [];
  let counter = 0;

  for (const [ref, group] of bySection) {
    const first = group[0];
    const page = locator.pageOf(first.index);
    const quote = locator.quoteAround(first.index, first.length, 220);
    const markers = [...new Set(group.map((g) => g.marker))];

    const absent = PERMITTED.map((p) => p.label);

    flags.push({
      id: `flag-${CHECK_ID}-${++counter}`,
      failureModeId: 'FM-08',
      tier: tierOf('FM-08'),
      layer: 'deterministic',
      checkId: CHECK_ID,
      title:
        group.length === 1
          ? `An estimated parameter with no cited source`
          : `${group.length} estimated parameters with no cited source`,
      quote,
      sectionRef: ref,
      page,
      quoteOffsets: locator.offsetsOf(quote, first.index),
      basis: detectionSignal('FM-08', 0),
      evidenceCaseIds: evidenceFor('FM-08'),
      receipt: {
        kind: 'lookup',
        inputs: [
          { label: 'Section', value: ref },
          { label: 'Page', value: page === null ? 'unknown' : String(page) },
          {
            label: 'Estimate markers found',
            value: `${group.length} (${markers.join(', ')})`,
          },
          { label: 'Search window', value: `±${WINDOW} characters around each marker` },
          {
            label:
              'Permitted sources sought — five from FEMP M&V Guidelines 5.0 §4.2.1.2, plus observation of the building, which this tool adds',
            value: absent.join('; '),
          },
        ],
        computation:
          'For each marker, search ±400 characters for any permitted source. Report where none is found.',
        result:
          group.length === 1
            ? 'No permitted source appears within 400 characters of the estimated value. None of the six permitted source types was found.'
            : `No permitted source appears within 400 characters of any of the ${group.length} estimated values. None of the six permitted source types was found.`,
      },
      status: 'open',
    });
  }

  return flags.map(enrichFlag);
}
